/**
 * Email Intake Queue Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  EmailIntakeQueueEntity,
  EmailIntakeQueueListEntity,
  EmailIntakeQueueResultEntity,
  EmailIntakeQueueOperationResult,
  PaginationInfo,
  EmailCategory,
} from "../../domain/entities/EmailIntakeQueueEntity";
import {
  EmailIntakeQueueDTO,
  EmailIntakeQueueResponseDTO,
  UpdateStatusResponseDTO,
  PaginationDTO,
} from "../models/EmailIntakeQueueDTOs";

export class EmailIntakeQueueMapper {
  /**
   * Parse email address to extract name and email
   */
  private static parseEmailAddress(emailStr: string): {
    name: string;
    email: string;
  } {
    const match = emailStr.match(/^(.+?)\s*<(.+)>$|^(.+)$/);
    if (match) {
      return {
        name: match[1] || match[3]?.split("@")[0] || "Unknown",
        email: match[2] || match[3] || emailStr,
      };
    }
    return { name: emailStr.split("@")[0] || "Unknown", email: emailStr };
  }

  /**
   * Map EmailIntakeQueueDTO to EmailIntakeQueueEntity
   */
  static toEmailIntakeQueueEntity(
    dto: EmailIntakeQueueDTO
  ): EmailIntakeQueueEntity {
    // Parse JSON fields
    let toEmails: string[] = [];
    try {
      toEmails =
        typeof dto.to_emails === "string"
          ? JSON.parse(dto.to_emails)
          : dto.to_emails;
      if (!Array.isArray(toEmails)) {
        toEmails = [];
      }
    } catch {
      toEmails = [];
    }

    let ccEmails: string[] | null = null;
    if (dto.cc_emails) {
      try {
        ccEmails =
          typeof dto.cc_emails === "string"
            ? JSON.parse(dto.cc_emails)
            : dto.cc_emails;
        if (!Array.isArray(ccEmails)) {
          ccEmails = null;
        }
      } catch {
        ccEmails = null;
      }
    }

    // Attachments is already an array of signed URLs (backend handles signing)
    let attachments: string[] = [];
    try {
      attachments =
        typeof dto.attachments === "string"
          ? JSON.parse(dto.attachments)
          : dto.attachments;
      if (!Array.isArray(attachments)) {
        attachments = [];
      }
    } catch {
      attachments = [];
    }

    let pushResults: any | null = null;
    if (dto.push_results) {
      try {
        pushResults =
          typeof dto.push_results === "string"
            ? JSON.parse(dto.push_results)
            : dto.push_results;
      } catch {
        pushResults = null;
      }
    }

    return {
      id: dto.id,
      message_id: dto.message_id,
      subdomain: dto.subdomain,
      category: dto.category as EmailCategory | null,
      from_email: dto.from_email,
      to_emails: toEmails,
      cc_emails: ccEmails,
      subject: dto.subject,
      email_text: dto.email_text,
      attachments: attachments,
      push_results: pushResults,
      status: dto.status as any,
      source: dto.source,
      bucket: dto.bucket,
      object_key: dto.object_key,
      received_at: dto.received_at,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
    };
  }

  /**
   * Map EmailIntakeQueueDTO to EmailIntakeQueueListEntity (with display fields)
   */
  static toEmailIntakeQueueListEntity(
    dto: EmailIntakeQueueDTO
  ): EmailIntakeQueueListEntity {
    const entity = this.toEmailIntakeQueueEntity(dto);

    // Parse sender name from email
    const senderInfo = this.parseEmailAddress(entity.from_email);

    return {
      ...entity,
      sender_name: senderInfo.name,
      attachment_count: entity.attachments ? entity.attachments.length : 0,
      reason: entity.category || undefined, // Option A: reason maps directly to category (convert null to undefined)
      client_name: entity.subdomain || undefined,
    };
  }

  /**
   * Map PaginationDTO to PaginationInfo
   */
  static toPaginationInfo(dto: PaginationDTO): PaginationInfo {
    return {
      total: dto.total,
      limit: dto.limit,
      offset: dto.offset,
      has_more: dto.has_more,
      total_pages: Math.ceil(dto.total / dto.limit),
      current_page: Math.floor(dto.offset / dto.limit) + 1,
    };
  }

  /**
   * Map EmailIntakeQueueResponseDTO to EmailIntakeQueueResultEntity
   */
  static toEmailIntakeQueueResultEntity(
    responseDto: EmailIntakeQueueResponseDTO
  ): EmailIntakeQueueResultEntity {
    return {
      emails: responseDto.data.map((dto) =>
        this.toEmailIntakeQueueListEntity(dto)
      ),
      pagination: this.toPaginationInfo(responseDto.pagination),
    };
  }

  /**
   * Map UpdateStatusResponseDTO to EmailIntakeQueueOperationResult
   */
  static toEmailIntakeQueueOperationResult(
    responseDto: UpdateStatusResponseDTO
  ): EmailIntakeQueueOperationResult {
    return {
      success: responseDto.success,
      message: responseDto.message,
      emails: responseDto.data.map((dto) => this.toEmailIntakeQueueEntity(dto)),
    };
  }
}
